import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthProvider';
import { useTranslation } from '../../i18n';
import { cn, displayNameOf } from '../../lib/utils';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { LanguageSwitcher } from '../controls/LanguageSwitcher';
import { ThemeToggle } from '../controls/ThemeToggle';
import { CloseIcon, LogoMark, LogOutIcon, MenuIcon, UserIcon } from '../icons';

function navLinkClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'rounded-md px-3 py-2 text-sm font-medium transition-colors',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand',
    isActive
      ? 'bg-brand-soft text-brand-soft-fg'
      : 'text-muted hover:bg-surface-muted hover:text-fg',
  );
}

/**
 * Application header.
 *
 * Layout: brand and primary navigation form a single left-aligned cluster, and
 * the controls (language, theme, session) sit at the trailing edge — so the
 * navigation reads as navigation rather than floating in the middle.
 * All spacing uses logical properties (`ms-auto`, `ps-`, `pe-`) so the layout
 * mirrors correctly in RTL.
 */
export function SiteHeader() {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [signingOut, setSigningOut] = useState(false);

  async function handleSignOut(): Promise<void> {
    setSigningOut(true);
    try {
      await logout();
      navigate('/');
    } finally {
      setSigningOut(false);
      setMenuOpen(false);
    }
  }

  const closeMenu = (): void => setMenuOpen(false);

  return (
    <header className="sticky top-0 z-40 border-b border-border bg-surface">
      <div className="mx-auto flex h-16 max-w-6xl items-center gap-2 px-4">
        <Link
          to="/"
          onClick={closeMenu}
          className="flex shrink-0 items-center gap-2 rounded-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <LogoMark className="h-8 w-8" />
          <span className="text-lg font-semibold tracking-tight text-fg">{t('app.name')}</span>
        </Link>

        <nav aria-label={t('nav.primary')} className="ms-2 hidden items-center gap-1 md:flex">
          <NavLink to="/" className={navLinkClass} end>
            {t('nav.home')}
          </NavLink>
          <NavLink to="/contests" className={navLinkClass}>
            {t('nav.contests')}
          </NavLink>
          {isAuthenticated && (
            <>
              <NavLink to="/dashboard" className={navLinkClass}>
                {t('nav.dashboard')}
              </NavLink>
              <NavLink to="/my-entries" className={navLinkClass}>
                {t('nav.myEntries')}
              </NavLink>
              <NavLink to="/profile" className={navLinkClass}>
                {t('nav.profile')}
              </NavLink>
            </>
          )}
          {isAdmin && (
            <>
              <NavLink to="/admin/contests" className={navLinkClass}>
                {t('nav.adminContests')}
              </NavLink>
              <NavLink to="/admin/finance" className={navLinkClass}>
                {t('nav.adminPayments')}
              </NavLink>
              <NavLink to="/admin/crypto" className={navLinkClass}>
                {t('nav.adminCrypto')}
              </NavLink>
              <NavLink to="/admin" className={navLinkClass} end>
                {t('nav.admin')}
              </NavLink>
            </>
          )}
        </nav>

        <div className="ms-auto flex items-center gap-1 sm:gap-2">
          <LanguageSwitcher />
          <ThemeToggle />

          <div className="hidden items-center gap-2 md:flex">
            {isAuthenticated && user ? (
              <>
                <span className="flex items-center gap-2 text-sm text-muted">
                  <UserIcon className="h-4 w-4" />
                  <span className="max-w-[10rem] truncate">{displayNameOf(user)}</span>
                  {isAdmin && <Badge tone="brand">{t('role.adminShort')}</Badge>}
                </span>
                <Button variant="outline" size="sm" onClick={handleSignOut} loading={signingOut}>
                  <LogOutIcon className="h-4 w-4" />
                  {t('nav.signOut')}
                </Button>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm">
                    {t('nav.signIn')}
                  </Button>
                </Link>
                <Link to="/register">
                  <Button size="sm">{t('nav.createAccount')}</Button>
                </Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-muted transition-colors hover:bg-surface-muted hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand md:hidden"
            aria-expanded={menuOpen}
            aria-controls="primary-mobile-menu"
            aria-label={menuOpen ? t('nav.closeMenu') : t('nav.openMenu')}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <CloseIcon /> : <MenuIcon />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div id="primary-mobile-menu" className="border-t border-border bg-surface md:hidden">
          <nav className="mx-auto flex max-w-6xl flex-col gap-1 px-4 py-3">
            <NavLink to="/" className={navLinkClass} end onClick={closeMenu}>
              {t('nav.home')}
            </NavLink>
            {isAuthenticated && (
              <>
                <NavLink to="/dashboard" className={navLinkClass} onClick={closeMenu}>
                  {t('nav.dashboard')}
                </NavLink>
                <NavLink to="/profile" className={navLinkClass} onClick={closeMenu}>
                  {t('nav.profile')}
                </NavLink>
              </>
            )}
            {isAdmin && (
              <NavLink to="/admin" className={navLinkClass} onClick={closeMenu}>
                {t('nav.admin')}
              </NavLink>
            )}

            <div className="mt-2 border-t border-border pt-3">
              {isAuthenticated && user ? (
                <div className="flex flex-col gap-3">
                  <div className="flex items-center gap-2 text-sm text-muted">
                    <UserIcon className="h-4 w-4" />
                    <span className="truncate">{displayNameOf(user)}</span>
                    {isAdmin && <Badge tone="brand">{t('role.adminShort')}</Badge>}
                  </div>
                  <Button variant="outline" onClick={handleSignOut} loading={signingOut} fullWidth>
                    <LogOutIcon className="h-4 w-4" />
                    {t('nav.signOut')}
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <Link to="/login" onClick={closeMenu}>
                    <Button variant="outline" fullWidth>
                      {t('nav.signIn')}
                    </Button>
                  </Link>
                  <Link to="/register" onClick={closeMenu}>
                    <Button fullWidth>{t('nav.createAccount')}</Button>
                  </Link>
                </div>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
